import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, BookMarked, Users, ArrowLeft, Search, User, FileText,
  BookOpen, ClipboardList, Presentation, PenSquare, Upload, Eye, Download, ExternalLink,
  GraduationCap, Clock, DollarSign, Trash2, Zap, Pencil
} from 'lucide-react';
import { Select as UISelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import FileViewerModal from '../../components/FileViewerModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const getToken = () => { try { return JSON.parse(localStorage.getItem('homeuni_auth') || '{}').token || ''; } catch { return ''; } };

const TYPE_CONFIG = {
  lesson: { label: 'Lesson Plan', icon: BookOpen, color: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
  assignment: { label: 'Assignment', icon: ClipboardList, color: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
  scheme_of_work: { label: 'Scheme of Work', icon: PenSquare, color: 'bg-purple-100 text-purple-700', border: 'border-purple-200' },
  lecture: { label: 'Lecture', icon: Presentation, color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200' },
  uploaded_file: { label: 'Uploaded File', icon: Upload, color: 'bg-slate-100 text-slate-600', border: 'border-slate-200' },
};

const PROG_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', hover: 'hover:border-blue-400 hover:shadow-md' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', hover: 'hover:border-emerald-400 hover:shadow-md' },
  { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', hover: 'hover:border-amber-400 hover:shadow-md' },
  { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', hover: 'hover:border-purple-400 hover:shadow-md' },
  { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-600', hover: 'hover:border-rose-400 hover:shadow-md' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'text-cyan-600', hover: 'hover:border-cyan-400 hover:shadow-md' },
];

const BLANK_PROG = { name: '', code: '', department: '', description: '', duration: '', price: '' };
const BLANK_COURSE = { title: '', code: '', description: '', week_number: 1 };
const CURRENT_YEAR = String(new Date().getFullYear());
const ADMISSION_WINDOWS = ['JAN', 'APR', 'JUL', 'OCT'];
const BLANK_COHORT = { name: '', programme_id: '', programme_name: '', admission_window: 'JAN', academic_year: `${CURRENT_YEAR}-${Number(CURRENT_YEAR)+1}` };

export default function AcademicsPage() {
  const [programmes, setProgrammes] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progDialog, setProgDialog] = useState(false);
  const [progForm, setProgForm] = useState(BLANK_PROG);
  const [courseDialog, setCourseDialog] = useState(false);
  const [courseForm, setCourseForm] = useState(BLANK_COURSE);

  // Drill-down state
  const [selectedProg, setSelectedProg] = useState(null);
  const [progTeachers, setProgTeachers] = useState([]);
  const [progCourses, setProgCourses] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherFiles, setTeacherFiles] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTab, setDrillTab] = useState('courses'); // 'courses' | 'lecturers'
  const [search, setSearch] = useState('');
  const [viewingFile, setViewingFile] = useState(null);
  const [contentViewer, setContentViewer] = useState(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);
  const [cohortDialog, setCohortDialog] = useState(false);
  const [cohortForm, setCohortForm] = useState(BLANK_COHORT);
  const [editCohortDialog, setEditCohortDialog] = useState(false);
  const [editCohortForm, setEditCohortForm] = useState(null);
  const [editProgDialog, setEditProgDialog] = useState(false);
  const [editProgForm, setEditProgForm] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [autoGenYear, setAutoGenYear] = useState(CURRENT_YEAR);
  const [autoGenLoading, setAutoGenLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [progRes, cohRes] = await Promise.all([
        api.get('/academic/programmes'),
        api.get('/academic/cohorts').catch(() => ({ data: [] })),
      ]);
      setProgrammes(progRes.data);
      setCohorts(cohRes.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  };

  const handleAddProgramme = async (e) => {
    e.preventDefault();
    try {
      await api.post('/academic/programmes', {
        ...progForm,
        price: parseFloat(progForm.price) || 0,
      });
      successToast('Programme created');
      setProgDialog(false);
      setProgForm(BLANK_PROG);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteProgramme = (progId, progName) => {
    setConfirm({
      open: true, title: `Delete "${progName}"?`,
      description: 'This will permanently remove the programme and all its courses.',
      onConfirm: async () => {
        try {
          await api.delete(`/academic/programmes/${progId}`);
          successToast('Programme deleted');
          fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      },
    });
  };

  const handleEditProgramme = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/academic/programmes/${editProgForm.programme_id}`, editProgForm);
      successToast('Programme updated');
      setEditProgDialog(false);
      setEditProgForm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeleteCohort = (cohortId, cohortName) => {
    setConfirm({
      open: true, title: `Delete cohort "${cohortName}"?`,
      description: 'This will permanently remove this cohort.',
      onConfirm: async () => {
        try {
          await api.delete(`/academic/cohorts/${cohortId}`);
          successToast('Cohort deleted');
          setCohorts(prev => prev.filter(c => c.cohort_id !== cohortId));
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete cohort'); }
      },
    });
  };

  const handleAddCohort = async (e) => {
    e.preventDefault();
    const prog = programmes.find(p => p.programme_id === cohortForm.programme_id);
    const payload = {
      ...cohortForm,
      programme_name: prog?.name || cohortForm.programme_name,
      name: cohortForm.name || `${prog?.name || 'Cohort'} — ${cohortForm.admission_window} ${cohortForm.academic_year.split('-')[0]}`,
    };
    try {
      await api.post('/academic/cohorts', payload);
      successToast('Cohort created');
      setCohortDialog(false);
      setCohortForm(BLANK_COHORT);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create cohort'); }
  };

  const handleUpdateCohort = async (e) => {
    e.preventDefault();
    const prog = programmes.find(p => p.programme_id === editCohortForm.programme_id);
    const payload = {
      ...editCohortForm,
      programme_name: prog?.name || editCohortForm.programme_name,
    };
    try {
      await api.put(`/academic/cohorts/${editCohortForm.cohort_id}`, payload);
      successToast('Cohort updated');
      setEditCohortDialog(false);
      setEditCohortForm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update cohort'); }
  };

  const handleAutoGenerate = async () => {
    setAutoGenLoading(true);
    try {
      const res = await api.post('/academic/cohorts/auto-generate', { year: autoGenYear });
      successToast(`Created ${res.data.created} cohort(s), skipped ${res.data.skipped} existing`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Auto-generate failed'); }
    setAutoGenLoading(false);
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/academic/programmes/${selectedProg.programme_id}/courses`, {
        ...courseForm,
        week_number: parseInt(courseForm.week_number) || 1,
        programme_id: selectedProg.programme_id,
      });
      successToast('Course added');
      setCourseDialog(false);
      setCourseForm(BLANK_COURSE);
      loadProgrammeCourses(selectedProg.programme_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCourse = (courseId) => {
    setConfirm({
      open: true, title: 'Delete this course?',
      description: 'This will permanently remove the course.',
      onConfirm: async () => {
        try {
          await api.delete(`/academic/programmes/${selectedProg.programme_id}/courses/${courseId}`);
          successToast('Course deleted');
          loadProgrammeCourses(selectedProg.programme_id);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      },
    });
  };

  const loadProgrammeCourses = async (progId) => {
    try {
      const r = await api.get(`/academic/programmes/${progId}/courses`);
      setProgCourses(r.data);
    } catch { setProgCourses([]); }
  };

  const openProgramme = async (prog) => {
    setSelectedProg(prog);
    setSelectedTeacher(null);
    setTeacherFiles([]);
    setDrillLoading(true);
    setDrillTab('courses');
    try {
      const [teachersRes, coursesRes] = await Promise.all([
        api.get(`/academic/subjects/${encodeURIComponent(prog.name)}/teachers`),
        api.get(`/academic/programmes/${prog.programme_id}/courses`),
      ]);
      setProgTeachers(teachersRes.data);
      setProgCourses(coursesRes.data);
    } catch { toast.error('Failed to load programme details'); }
    setDrillLoading(false);
  };

  const openTeacher = async (teacher) => {
    setSelectedTeacher(teacher);
    setActiveTypeFilter(null);
    setDrillLoading(true);
    try {
      const r = await api.get(`/academic/subjects/${encodeURIComponent(selectedProg.name)}/teacher/${teacher.user_id}/files`);
      setTeacherFiles(r.data);
    } catch { toast.error('Failed to load files'); setTeacherFiles([]); }
    setDrillLoading(false);
  };

  const goBack = () => {
    if (selectedTeacher) { setSelectedTeacher(null); setTeacherFiles([]); }
    else { setSelectedProg(null); setProgTeachers([]); setProgCourses([]); }
  };

  const openFilePreview = (fileId) => {
    const token = getToken();
    setViewingFile(`${BACKEND}/api/files/${fileId}/download?token=${token}`);
  };

  const openDocument = async (doc) => {
    const allFileIds = [
      ...(doc.file_ids || []).map(f => typeof f === 'string' ? f : f?.file_id).filter(Boolean),
      ...(doc.original_file_id ? [doc.original_file_id] : []),
    ];
    if (doc.type === 'uploaded_file' && allFileIds.length > 0) { openFilePreview(allFileIds[0]); return; }
    setContentViewer({ doc, content: null, loading: true, fileIds: allFileIds });
    try {
      let endpoint;
      if (doc.type === 'scheme_of_work') endpoint = 'schemes';
      else if (doc.type === 'lesson') endpoint = 'lessons-v2';
      else if (doc.type === 'lecture') endpoint = 'lectures';
      else endpoint = 'assignments';
      const r = await api.get(`/${endpoint}/${doc.doc_id}`);
      const data = r.data;
      let html = '';
      if (doc.type === 'scheme_of_work') {
        html = data.rich_content || '';
        if (!html && data.weeks?.length > 0) {
          html = '<table style="width:100%;border-collapse:collapse;"><thead><tr style="background:#1565C0;color:white;"><th style="padding:8px;border:1px solid #334155;">Week</th><th style="padding:8px;border:1px solid #334155;">Topic</th><th style="padding:8px;border:1px solid #334155;">Objectives</th><th style="padding:8px;border:1px solid #334155;">Activities</th><th style="padding:8px;border:1px solid #334155;">Assessment</th><th style="padding:8px;border:1px solid #334155;">Resources</th></tr></thead><tbody>' +
            data.weeks.map((w, i) => `<tr><td style="padding:8px;border:1px solid #e2e8f0;text-align:center;">${w.week_number||i+1}</td><td style="padding:8px;border:1px solid #e2e8f0;">${w.topic||''}</td><td style="padding:8px;border:1px solid #e2e8f0;">${w.objectives||''}</td><td style="padding:8px;border:1px solid #e2e8f0;">${w.activities||''}</td><td style="padding:8px;border:1px solid #e2e8f0;">${w.assessment||''}</td><td style="padding:8px;border:1px solid #e2e8f0;">${w.resources||''}</td></tr>`).join('') + '</tbody></table>';
        }
        if (html) html = `<div style="margin-bottom:16px;padding:12px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0;"><h2 style="margin:0;color:#1565C0;">${data.title||data.subject_name||'Scheme of Work'}</h2><p style="margin:4px 0 0;color:#64748b;font-size:13px;">${data.subject_name||''} | ${data.term||''} | ${data.teacher_name||''}</p></div>` + html;
      } else if (doc.type === 'lesson') {
        html = data.rich_content || data.content_html || data.content_text || '';
        if (html) html = `<div style="margin-bottom:16px;padding:12px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0;"><h2 style="margin:0;color:#1565C0;">${data.title||'Lesson Plan'}</h2></div>` + html;
      } else if (doc.type === 'lecture') {
        html = data.rich_content || (data.slides?.map((s,i)=>`<div style="margin-bottom:24px;padding:16px;border:1px solid #e2e8f0;border-radius:8px;"><h3 style="margin:0 0 8px;color:#1565C0;">Slide ${i+1}${s.title?': '+s.title:''}</h3><div>${s.content||''}</div></div>`).join('') || '');
      } else {
        const parts = [data.instructions, data.description, data.rich_content].filter(Boolean);
        html = parts.join('<hr style="margin:12px 0"/>');
      }
      if (!html) html = '<div style="text-align:center;padding:40px 0;color:#94a3b8;">No viewable content — check attached files</div>';
      setContentViewer(prev => ({ ...prev, content: html, loading: false }));
    } catch (err) {
      setContentViewer(prev => ({ ...prev, content: `<p style="color:#ef4444;text-align:center;padding:40px 0">Failed to load: ${err.response?.data?.detail||'Unknown error'}</p>`, loading: false }));
    }
  };

  const filtered = search
    ? programmes.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || (p.code || '').toLowerCase().includes(search.toLowerCase()) || (p.department || '').toLowerCase().includes(search.toLowerCase()))
    : programmes;

  if (loading) return <HomeUniLoader />;

  // ─── LEVEL 3: Lecturer's Files ───
  if (selectedProg && selectedTeacher) {
    const filesByType = {};
    teacherFiles.forEach(f => { if (!filesByType[f.type]) filesByType[f.type] = []; filesByType[f.type].push(f); });
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1565C0]">{selectedTeacher.name}</h1>
            <p className="text-sm text-slate-500">{selectedProg.name} · All Documents & Files</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const count = (filesByType[key] || []).length;
            const isActive = activeTypeFilter === key;
            return (
              <Card key={key} className={`rounded-md border cursor-pointer transition-all ${isActive ? `${cfg.border} ring-2` : cfg.border} ${count===0?'opacity-50':''}`} onClick={() => count > 0 && setActiveTypeFilter(isActive ? null : key)}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-md ${cfg.color} flex items-center justify-center`}><cfg.icon className="w-4 h-4" /></div>
                  <div><p className="text-lg font-bold text-[#1565C0]">{count}</p><p className="text-[10px] text-slate-500">{cfg.label}s</p></div>
                  {count > 0 && <Eye className="w-3.5 h-3.5 text-slate-300 ml-auto" />}
                </CardContent>
              </Card>
            );
          })}
        </div>
        {activeTypeFilter && (
          <div className="flex items-center gap-2">
            <Badge className={`${(TYPE_CONFIG[activeTypeFilter]||{}).color||'bg-slate-100'} text-xs`}>Showing: {(TYPE_CONFIG[activeTypeFilter]||{}).label}s</Badge>
            <button onClick={() => setActiveTypeFilter(null)} className="text-xs text-slate-400 hover:text-slate-600 underline">Show all</button>
          </div>
        )}
        {drillLoading ? <HomeUniLoader /> : teacherFiles.length === 0 ? (
          <div className="text-center py-16 text-slate-400"><FileText className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">No documents found</p></div>
        ) : (
          <div className="space-y-6">
            {Object.entries(filesByType).filter(([type]) => !activeTypeFilter || type === activeTypeFilter).map(([type, docs]) => {
              const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.uploaded_file;
              return (
                <div key={type}>
                  <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><cfg.icon className="w-4 h-4" /> {cfg.label}s ({docs.length})</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {docs.map(doc => (
                      <Card key={doc.doc_id} className={`rounded-md border ${cfg.border} hover:shadow-md cursor-pointer`} onClick={() => openDocument(doc)}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-md ${cfg.color} flex items-center justify-center flex-shrink-0`}><cfg.icon className="w-5 h-5" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#1565C0] truncate">{doc.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {doc.status && <Badge className={`text-[9px] ${doc.status==='approved'||doc.status==='published'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{doc.status}</Badge>}
                              </div>
                              {doc.created_at && <p className="text-[10px] text-slate-400 mt-1">{new Date(doc.created_at).toLocaleDateString('en-GB')}</p>}
                            </div>
                            <Eye className="w-4 h-4 text-slate-300 flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {viewingFile && <FileViewerModal url={viewingFile} onClose={() => setViewingFile(null)} />}
        <Dialog open={!!contentViewer} onOpenChange={v => { if (!v) setContentViewer(null); }}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {contentViewer?.doc && (() => {
                  const cfg = TYPE_CONFIG[contentViewer.doc.type] || TYPE_CONFIG.uploaded_file;
                  return <><div className={`w-7 h-7 rounded-md ${cfg.color} flex items-center justify-center`}><cfg.icon className="w-4 h-4" /></div><span className="truncate">{contentViewer.doc.title}</span></>;
                })()}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 border rounded-md bg-white">
              {contentViewer?.loading ? <div className="flex items-center justify-center py-20"><HomeUniLoader /></div> : (
                <div className="p-6" style={{ fontSize: '14px', lineHeight: '1.7' }}>
                  <style>{`.doc-content table{width:100%;border-collapse:collapse;margin:12px 0}.doc-content table th{background:#1565C0;color:white;padding:10px 12px;text-align:left;border:1px solid #334155}.doc-content table td{padding:10px 12px;border:1px solid #e2e8f0;font-size:13px;vertical-align:top}.doc-content table tr:nth-child(even){background:#f8fafc}.doc-content h1,.doc-content h2,.doc-content h3{color:#1565C0;margin-top:16px}`}</style>
                  <div className="doc-content" dangerouslySetInnerHTML={{ __html: contentViewer?.content || '' }} />
                </div>
              )}
            </div>
            {contentViewer?.fileIds?.length > 0 && (
              <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                <span className="text-[10px] text-slate-500 uppercase font-bold self-center mr-1">Attached Files:</span>
                {contentViewer.fileIds.map((fid, fi) => (
                  <Button key={fi} size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setContentViewer(null); openFilePreview(fid); }}><FileText className="w-3.5 h-3.5 mr-1" /> Open File {fi + 1}</Button>
                ))}
                {contentViewer.fileIds.map((fid, fi) => (
                  <a key={`dl-${fi}`} href={`${BACKEND}/api/files/${fid}/download?token=${getToken()}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-[#1976D2]"><Download className="w-3.5 h-3.5 mr-1" /> Download {fi + 1}</Button>
                  </a>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── LEVEL 2: Programme Detail (Courses + Lecturers) ───
  if (selectedProg) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-1" /> Back to Programmes</Button>
            <div>
              <h1 className="text-2xl font-bold text-[#1565C0]">{selectedProg.name}</h1>
              <p className="text-sm text-slate-500">{selectedProg.code} · {selectedProg.department || 'General'} · {selectedProg.duration || ''} {selectedProg.price ? `· GHS ${Number(selectedProg.price).toLocaleString()}` : ''}</p>
            </div>
          </div>
        </div>

        <Tabs value={drillTab} onValueChange={setDrillTab}>
          <TabsList className="bg-white border border-[#BBDEFB]">
            <TabsTrigger value="courses">Courses ({progCourses.length})</TabsTrigger>
            <TabsTrigger value="lecturers">Lecturers ({progTeachers.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
                <DialogTrigger asChild>
                  <Button className="rounded-xl btn-navy"><Plus className="w-4 h-4 mr-2" /> Add Course</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Course to {selectedProg.name}</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddCourse} className="space-y-4">
                    <div className="space-y-2"><Label>Course Title</Label><Input value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} required placeholder="e.g. Corporate Finance" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Code</Label><Input value={courseForm.code} onChange={e => setCourseForm({...courseForm, code: e.target.value})} required placeholder="e.g. MBA-101" /></div>
                      <div className="space-y-2"><Label>Week Number</Label><Input type="number" min={1} value={courseForm.week_number} onChange={e => setCourseForm({...courseForm, week_number: e.target.value})} /></div>
                    </div>
                    <div className="space-y-2"><Label>Description</Label><Input value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} placeholder="Brief course description" /></div>
                    <Button type="submit" className="w-full rounded-xl btn-navy">Add Course</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {drillLoading ? <HomeUniLoader /> : progCourses.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No courses added yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#BBDEFB] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Course Title</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Week</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {progCourses.map((c, i) => (
                      <TableRow key={c.course_id}>
                        <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                        <TableCell className="font-medium text-[#1565C0]">{c.title}</TableCell>
                        <TableCell><Badge variant="secondary">{c.code}</Badge></TableCell>
                        <TableCell className="text-slate-500 text-sm">Week {c.week_number}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[240px] truncate">{c.description || '—'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteCourse(c.course_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="lecturers" className="mt-4">
            {drillLoading ? <HomeUniLoader /> : progTeachers.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No lecturers found for {selectedProg.name}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {progTeachers.map(t => (
                  <Card key={t.user_id} className="rounded-md border border-slate-200 hover:border-[#2E6CC4] hover:shadow-md cursor-pointer" onClick={() => openTeacher(t)}>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#1565C0] flex items-center justify-center text-white font-bold text-lg">{t.name?.charAt(0) || '?'}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-[#1565C0] truncate">{t.name}</p>
                        <p className="text-xs text-slate-400">{t.department || 'Lecturer'}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-300" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ─── LEVEL 1: Programme Tiles + Cohorts Tab ───
  return (
    <div className="space-y-6" data-testid="academics-page">
      <h1 className="text-2xl font-bold text-[#1565C0]">Academic Management</h1>

      <Tabs defaultValue="programmes">
        <TabsList className="bg-white border" data-testid="academics-tabs">
          <TabsTrigger value="programmes" data-testid="programmes-tab">Programmes ({programmes.length})</TabsTrigger>
          <TabsTrigger value="cohorts" data-testid="cohorts-tab">Cohorts ({cohorts.length})</TabsTrigger>
        </TabsList>

        {/* ── Programmes Tab ── */}
        <TabsContent value="programmes" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search programmes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Dialog open={progDialog} onOpenChange={setProgDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-xl btn-navy" data-testid="add-programme-btn"><Plus className="w-4 h-4 mr-2" /> Add Programme</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Programme</DialogTitle></DialogHeader>
                <form onSubmit={handleAddProgramme} className="space-y-4">
                  <div className="space-y-2"><Label>Programme Name</Label><Input value={progForm.name} onChange={e => setProgForm({...progForm, name: e.target.value})} required placeholder="e.g. Chartered MBA" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Code</Label><Input value={progForm.code} onChange={e => setProgForm({...progForm, code: e.target.value})} required placeholder="e.g. MBA" /></div>
                    <div className="space-y-2"><Label>Department</Label><Input value={progForm.department} onChange={e => setProgForm({...progForm, department: e.target.value})} placeholder="e.g. Business" /></div>
                  </div>
                  <div className="space-y-2"><Label>Description</Label><Input value={progForm.description} onChange={e => setProgForm({...progForm, description: e.target.value})} placeholder="Brief programme description" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Duration</Label><Input value={progForm.duration} onChange={e => setProgForm({...progForm, duration: e.target.value})} placeholder="e.g. 18 months" /></div>
                    <div className="space-y-2"><Label>Price (GHS)</Label><Input type="number" min="0" step="0.01" value={progForm.price} onChange={e => setProgForm({...progForm, price: e.target.value})} placeholder="0.00" /></div>
                  </div>
                  <Button type="submit" className="w-full rounded-xl btn-navy">Create Programme</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p, i) => {
              const c = PROG_COLORS[i % PROG_COLORS.length];
              return (
                <div key={p.programme_id} className={`${c.bg} border ${c.border} rounded-xl p-5 cursor-pointer transition-all ${c.hover} group relative`} onClick={() => openProgramme(p)} data-testid={`programme-tile-${p.programme_id}`}>
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 z-10">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#1565C0] hover:bg-blue-50" onClick={e => { e.stopPropagation(); setEditProgForm({...p}); setEditProgDialog(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50" onClick={e => { e.stopPropagation(); handleDeleteProgramme(p.programme_id, p.name); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className={`w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center mb-3 shadow-sm`}>
                    <GraduationCap className={`w-5 h-5 ${c.icon}`} />
                  </div>
                  <h3 className="text-base font-bold text-[#1B2A5E] mb-1 pr-6">{p.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-[9px] bg-white/60">{p.code}</Badge>
                    {p.department && <span className="text-[10px] text-slate-500">{p.department}</span>}
                  </div>
                  {p.description && <p className="text-[11px] text-slate-500 line-clamp-2 mb-3">{p.description}</p>}
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    {p.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration}</span>}
                    {p.price > 0 && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> GHS {Number(p.price).toLocaleString()}</span>}
                    <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {p.course_count || 0} courses</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{search ? 'No programmes match your search' : 'No programmes added yet'}</p>
            </div>
          )}
        </TabsContent>

        {/* ── Cohorts Tab ── */}
        <TabsContent value="cohorts" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <p className="text-sm text-slate-500">{cohorts.length} cohort(s) total</p>
            <div className="flex gap-2 flex-wrap items-center">
              {/* Auto-generate row */}
              <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <span className="text-xs text-slate-500">Auto-generate for year:</span>
                <input type="number" value={autoGenYear} onChange={e => setAutoGenYear(e.target.value)} className="w-20 text-xs border-0 outline-none font-medium" min="2025" max="2035" />
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={handleAutoGenerate} disabled={autoGenLoading} data-testid="auto-gen-cohorts-btn">
                  <Zap className="w-3 h-3 mr-1" />{autoGenLoading ? 'Generating...' : 'Auto-Generate'}
                </Button>
              </div>
              <Dialog open={cohortDialog} onOpenChange={setCohortDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-lg btn-navy h-8 text-xs" data-testid="add-cohort-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Cohort</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create New Cohort</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddCohort} className="space-y-4">
                    <div className="space-y-2"><Label>Programme</Label>
                      <UISelect value={cohortForm.programme_id} onValueChange={v => setCohortForm({...cohortForm, programme_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select programme" /></SelectTrigger>
                        <SelectContent>{programmes.map(p => <SelectItem key={p.programme_id} value={p.programme_id}>{p.name}</SelectItem>)}</SelectContent>
                      </UISelect>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Admission Window</Label>
                        <UISelect value={cohortForm.admission_window} onValueChange={v => setCohortForm({...cohortForm, admission_window: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{ADMISSION_WINDOWS.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                        </UISelect>
                      </div>
                      <div className="space-y-2"><Label>Academic Year</Label>
                        <input value={cohortForm.academic_year} onChange={e => setCohortForm({...cohortForm, academic_year: e.target.value})} placeholder="e.g. 2026-2027" className="w-full border rounded-md px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <div className="space-y-2"><Label>Cohort Name (optional — auto-generated if blank)</Label>
                      <Input value={cohortForm.name} onChange={e => setCohortForm({...cohortForm, name: e.target.value})} placeholder="Leave blank to auto-name" />
                    </div>
                    <Button type="submit" className="w-full btn-navy">Create Cohort</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#BBDEFB] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-[#EFF6FF]">Cohort Name</TableHead>
                  <TableHead className="bg-[#EFF6FF]">Programme</TableHead>
                  <TableHead className="bg-[#EFF6FF]">Window</TableHead>
                  <TableHead className="bg-[#EFF6FF]">Academic Year</TableHead>
                  <TableHead className="bg-[#EFF6FF]">Status</TableHead>
                  <TableHead className="bg-[#EFF6FF] w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No cohorts found</TableCell></TableRow>
                ) : cohorts.map(c => (
                  <TableRow key={c.cohort_id}>
                    <TableCell className="font-medium flex items-center gap-2"><Users className="w-4 h-4 text-[#1565C0]" /> {c.name}</TableCell>
                    <TableCell className="text-xs text-slate-600">{c.programme_name || '—'}</TableCell>
                    <TableCell><Badge className="bg-blue-100 text-blue-700">{c.admission_window}</Badge></TableCell>
                    <TableCell><Badge variant="secondary">{c.academic_year}</Badge></TableCell>
                    <TableCell><Badge className={c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{c.status || 'active'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => { setEditCohortForm({...c}); setEditCohortDialog(true); }}
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 hover:text-red-500"
                          onClick={() => handleDeleteCohort(c.cohort_id, c.name)}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-slate-300 hover:text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Edit Cohort Dialog ── */}
      <Dialog open={editCohortDialog} onOpenChange={setEditCohortDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Cohort</DialogTitle></DialogHeader>
          {editCohortForm && (
            <form onSubmit={handleUpdateCohort} className="space-y-4">
              <div className="space-y-2"><Label>Programme</Label>
                <UISelect value={editCohortForm.programme_id} onValueChange={v => setEditCohortForm({...editCohortForm, programme_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select programme" /></SelectTrigger>
                  <SelectContent>{programmes.map(p => <SelectItem key={p.programme_id} value={p.programme_id}>{p.name}</SelectItem>)}</SelectContent>
                </UISelect>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Admission Window</Label>
                  <UISelect value={editCohortForm.admission_window} onValueChange={v => setEditCohortForm({...editCohortForm, admission_window: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ADMISSION_WINDOWS.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </UISelect>
                </div>
                <div className="space-y-2"><Label>Academic Year</Label>
                  <Input value={editCohortForm.academic_year} onChange={e => setEditCohortForm({...editCohortForm, academic_year: e.target.value})} placeholder="e.g. 2026-2027" />
                </div>
              </div>
              <div className="space-y-2"><Label>Cohort Name</Label>
                <Input value={editCohortForm.name} onChange={e => setEditCohortForm({...editCohortForm, name: e.target.value})} />
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <UISelect value={editCohortForm.status || 'active'} onValueChange={v => setEditCohortForm({...editCohortForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </UISelect>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setEditCohortDialog(false)}>Cancel</Button>
                <Button type="submit" className="btn-navy">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirm.open}
        onOpenChange={open => setConfirm(s => ({ ...s, open }))}
        title={confirm.title}
        description={confirm.description}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirm.onConfirm || (() => {})}
      />

      {/* ── Edit Programme Dialog ── */}
      <Dialog open={editProgDialog} onOpenChange={setEditProgDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Programme</DialogTitle></DialogHeader>
          {editProgForm && (
            <form onSubmit={handleEditProgramme} className="space-y-4">
              <div className="space-y-1.5"><Label>Programme Name</Label>
                <Input value={editProgForm.name || ''} onChange={e => setEditProgForm({...editProgForm, name: e.target.value})} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Code</Label>
                  <Input value={editProgForm.code || ''} onChange={e => setEditProgForm({...editProgForm, code: e.target.value})} placeholder="e.g. BBA" />
                </div>
                <div className="space-y-1.5"><Label>Duration</Label>
                  <Input value={editProgForm.duration || ''} onChange={e => setEditProgForm({...editProgForm, duration: e.target.value})} placeholder="e.g. 3 years" />
                </div>
                <div className="space-y-1.5"><Label>Department / Faculty</Label>
                  <Input value={editProgForm.department || ''} onChange={e => setEditProgForm({...editProgForm, department: e.target.value})} />
                </div>
                <div className="space-y-1.5"><Label>Price (GHS)</Label>
                  <Input type="number" value={editProgForm.price || ''} onChange={e => setEditProgForm({...editProgForm, price: e.target.value})} />
                </div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label>
                <textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]" value={editProgForm.description || ''} onChange={e => setEditProgForm({...editProgForm, description: e.target.value})} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setEditProgDialog(false)}>Cancel</Button>
                <Button type="submit" className="btn-navy">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
